/**
 * <s-auto-slider> gently advances the homepage New Arrivals row one card at a time (merchant request, Nov 2, 2026).
 * One card every 3.5 seconds with a slow 1.2s glide (merchant asked to slow it, Nov 2), rewinding to the first card at the end. It keeps moving under the mouse; it pauses while the shopper
 * touches, scrolls or tabs into the row, while the row is off screen or the browser tab is hidden, and for
 * good after the shopper presses Pause. It never moves when the shopper's device asks for reduced motion.
 * Previous / Next buttons move one card. Works with s-tabs: it always drives the visible panel's list.
 */
class SAutoSlider extends HTMLElement {
  #ac;
  #timer = null;
  #hovering = false;
  #focused = false;
  #inView = false;
  #userPaused = false;
  #touchHoldUntil = 0;
  #observer;
  #raf = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.interval = Math.max(800, Number(this.getAttribute("interval")) || 1000);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Phones (under 768px) get a plain swipe row: no auto-advance (merchant choice, Nov 2).
    this.wide = window.matchMedia('(min-width: 768px)');
    this.wide.addEventListener?.('change', () => this.#schedule());

    this.toggleButton = this.querySelector('[data-slide-toggle]');
    this.querySelector('[data-slide-prev]')?.addEventListener('click', () => this.#step(-1, true), { signal });
    this.querySelector('[data-slide-next]')?.addEventListener('click', () => this.#step(1, true), { signal });
    this.toggleButton?.addEventListener('click', () => {
      this.#userPaused = !this.#userPaused;
      this.#syncToggle();
      this.#schedule();
    }, { signal });

    const area = this.querySelector('[data-slide-area]') || this;
    area.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, { signal });
    area.addEventListener('focusout', (event) => {
      if (!area.contains(event.relatedTarget)) { this.#focused = false; this.#schedule(); }
    }, { signal });
    // A touch or wheel swipe means the shopper is browsing: hold off for a while, then resume.
    const hold = () => { this.#touchHoldUntil = Date.now() + 4000; this.#schedule(); };
    area.addEventListener('pointerdown', hold, { signal, passive: true });
    area.addEventListener('wheel', hold, { signal, passive: true });
    area.addEventListener('touchstart', hold, { signal, passive: true });

    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });
    this.reduced.addEventListener?.('change', () => { this.#syncToggle(); this.#schedule(); }, { signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#inView = entry.isIntersecting;
      this.#schedule();
    }, { threshold: 0.1 });
    this.#observer.observe(area);

    this.#syncToggle();
    this.#schedule();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }

  get #list() {
    const lists = this.querySelectorAll('[data-autoslide]');
    for (const list of lists) {
      if (!list.closest('[hidden]') && list.offsetParent !== null) return list;
    }
    return null;
  }

  #canAutoplay() {
    return this.wide.matches && !this.reduced.matches && !this.#userPaused && !this.#hovering && !this.#focused &&
      this.#inView && document.visibilityState === 'visible';
  }

  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = null;
    if (!this.#canAutoplay()) return;
    const wait = Math.max(this.interval, this.#touchHoldUntil - Date.now());
    this.#timer = setTimeout(() => {
      if (this.#canAutoplay()) this.#step(1, false);
      this.#schedule();
    }, wait);
  }

  #step(direction, fromButton) {
    const list = this.#list;
    if (!list) return;
    const card = list.querySelector(':scope > li');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const distance = card.getBoundingClientRect().width + gap;
    const maxScroll = list.scrollWidth - list.clientWidth;
    if (maxScroll <= 2) return;
    const rtl = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
    const current = Math.abs(list.scrollLeft);
    let target;
    if (direction > 0) {
      target = current >= maxScroll - 4 ? 0 : Math.min(current + distance, maxScroll);
    } else {
      target = current <= 4 ? maxScroll : Math.max(current - distance, 0);
    }
    this.#glide(list, target * rtl);
    if (fromButton) { this.#touchHoldUntil = Date.now() + 4000; this.#schedule(); }
  }

  // Slow, eased glide (about 1.2s) instead of the browser's quick native smooth scroll. Snap is lifted during the
  // glide so it doesn't jump, then restored. Reduced motion jumps straight to the card.
  #glide(list, left) {
    cancelAnimationFrame(this.#raf);
    if (this.reduced.matches) { list.scrollLeft = left; return; }
    const start = list.scrollLeft;
    const change = left - start;
    const duration = 1200;
    const began = performance.now();
    list.style.scrollSnapType = 'none';
    list.style.scrollBehavior = 'auto';
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const frame = (now) => {
      const t = Math.min(1, (now - began) / duration);
      list.scrollLeft = start + change * ease(t);
      if (t < 1) { this.#raf = requestAnimationFrame(frame); return; }
      list.style.scrollSnapType = '';
      list.style.scrollBehavior = '';
    };
    this.#raf = requestAnimationFrame(frame);
  }

  #syncToggle() {
    if (!this.toggleButton) return;
    const playing = !this.#userPaused && !this.reduced.matches;
    this.toggleButton.hidden = this.reduced.matches;
    this.toggleButton.setAttribute('aria-pressed', String(this.#userPaused));
    this.toggleButton.setAttribute('aria-label', playing ? this.dataset.pauseLabel || 'Pause' : this.dataset.playLabel || 'Play');
    this.toggleButton.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
    this.toggleButton.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
  }
}

if (!customElements.get('s-auto-slider')) customElements.define('s-auto-slider', SAutoSlider);
